import { AlertTriangle, ArrowLeft, ChevronDown, Copy, Filter, LayoutGrid, List, LoaderCircle, Plus, Search, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Department, type Project, type Task, type User } from "../api";
import { ddayText, ddayTone, difficultyText, isoDate, statusText } from "../lib/format";
import { TaskDeadline } from "../components/TaskDeadline";
import { departmentWithin, taskNeedsApproval } from "../lib/org";
import { Avatar, Empty, KanbanPatternBar, Loading, PageHead, TableFoot } from "../components/ui";

type Lane = "초안" | "준비" | "진행" | "검증" | "완료";
type Bucket = "초안" | "내 차례" | "진행 중" | "판정 대기" | "검증" | "완료 대기" | "완료" | "취소";
type TaskScope = { team: boolean; projects: boolean; departmentIds: string[] };
type FilterOperator = "include" | "exclude";
type TaskFilterPreferences = { scope: TaskScope; filters: Record<string, string[]>; operators: Record<string, FilterOperator>; dueFrom: string; dueTo: string };

const defaultTaskFilters: TaskFilterPreferences = { scope: { team: false, projects: false, departmentIds: [] }, filters: {}, operators: {}, dueFrom: "", dueTo: "" };
const taskFilterKey = (viewerId: string) => `angelflow.task-board.filters.v1:${viewerId || "anonymous"}`;
const NO_PROJECT = "__no_project__";

function savedTaskFilters(viewerId: string): TaskFilterPreferences {
  try {
    const saved = JSON.parse(localStorage.getItem(taskFilterKey(viewerId)) || "null") as Partial<TaskFilterPreferences> | null;
    return saved ? {
      scope: {
        team: saved.scope?.team === true,
        projects: saved.scope?.projects === true,
        departmentIds: Array.isArray(saved.scope?.departmentIds) ? saved.scope.departmentIds.filter((id): id is string => typeof id === "string") : [],
      },
      filters: saved.filters && typeof saved.filters === "object" ? Object.fromEntries(Object.entries(saved.filters)
        .filter((entry): entry is [string, string[]] => Array.isArray(entry[1]))
        .map(([key, values]) => [key, values.filter((value): value is string => typeof value === "string")])) : {},
      operators: saved.operators && typeof saved.operators === "object" ? Object.fromEntries(Object.entries(saved.operators)
        .filter((entry): entry is [string, FilterOperator] => entry[1] === "include" || entry[1] === "exclude")) : {},
      dueFrom: typeof saved.dueFrom === "string" ? saved.dueFrom : "",
      dueTo: typeof saved.dueTo === "string" ? saved.dueTo : "",
    } : defaultTaskFilters;
  } catch { return defaultTaskFilters; }
}

const laneOrder: Lane[] = ["초안", "준비", "진행", "검증", "완료"];
const bucketOptions: Bucket[] = ["내 차례", "진행 중", "판정 대기", "검증", "완료 대기", "완료", "초안"];

export function taskBucket(task: Task): Bucket {
  if (task.status === "CANCELLED") return "취소";
  if (task.status === "COMPLETED") return "완료";
  if (task.status === "DRAFT") return "초안";
  if (task.status === "VERIFYING") return "검증";
  if (task.status === "COMPLETION_REVIEW") return "완료 대기";
  // 목록 API는 회차 본문을 싣지 않으므로 판정 대기는 업무 상태를 기준으로 판별한다.
  if (task.status === "IN_REVIEW") return "판정 대기";
  const round = task.rounds.at(-1);
  if (round?.status === "SUBMITTED") return "판정 대기";
  if (round?.status === "REVISION_REQUIRED") return "내 차례";
  if (task.status === "IN_PROGRESS") return "진행 중";
  return "내 차례";
}

export function taskLane(task: Task): Lane | null {
  if (task.status === "CANCELLED") return null;
  if (task.status === "COMPLETED") return "완료";
  if (task.status === "DRAFT") return "초안";
  if (task.status === "VERIFYING") return "검증";
  if (task.status === "COMPLETION_REVIEW") return "검증";
  if (task.status === "IN_REVIEW") return "진행";
  if (task.rounds.at(-1)?.status === "SUBMITTED") return task.rounds.at(-1)?.kind === "VERIFICATION" ? "검증" : "진행";
  if (task.status === "IN_PROGRESS") return "진행";
  return "준비";
}

export function taskStateText(task: Task, viewerId?: string): string {
  if (task.status === "DRAFT" && ["QUEUED", "GENERATING", "CANCELLING"].includes(task.aiDraftStatus || "")) return "AI 작성 중";
  if (task.status === "DRAFT" && task.aiDraftStatus === "FAILED") return "생성 실패";
  if (task.status === "IN_REVIEW") return "판정 대기";
  if (task.status === "VERIFYING") return "검증 중";
  if (task.status === "COMPLETION_REVIEW") return "완료 대기";
  if (task.status === "REQUESTED" && !task.collaborationSourceDepartmentId) return viewerId && task.issuerId !== viewerId ? "제안 승인" : "제안 승인 대기";
  if (task.status === "IN_PROGRESS" && task.scheduleChangeRequest) return task.scheduleChangeActionRequired ? "일정 변경 승인" : "일정 변경 대기";
  const round = task.rounds.at(-1);
  if (task.status === "IN_PROGRESS" && round?.status === "REVISION_REQUIRED") return "보완 요청";
  if (round?.status === "SUBMITTED") return "판정 대기";
  if (task.status === "CONFIRMED") return "신규 지시";
  return statusText[task.status];
}

export function taskStateKey(task: Task, viewerId?: string): string {
  if (task.status === "DRAFT" && ["QUEUED", "GENERATING", "CANCELLING"].includes(task.aiDraftStatus || "")) return "tasks.state.aiWriting";
  if (task.status === "DRAFT" && task.aiDraftStatus === "FAILED") return "tasks.state.aiFailed";
  if (task.status === "IN_REVIEW" || task.rounds.at(-1)?.status === "SUBMITTED") return "tasks.state.review";
  if (task.status === "VERIFYING") return "tasks.state.verifying";
  if (task.status === "COMPLETION_REVIEW") return "tasks.state.completionReview";
  if (task.status === "REQUESTED" && !task.collaborationSourceDepartmentId) return viewerId && task.issuerId !== viewerId ? "tasks.state.proposalApproval" : "tasks.state.proposalPending";
  if (task.status === "IN_PROGRESS" && task.scheduleChangeRequest) return task.scheduleChangeActionRequired ? "tasks.state.scheduleApproval" : "tasks.state.schedulePending";
  if (task.status === "IN_PROGRESS" && task.rounds.at(-1)?.status === "REVISION_REQUIRED") return "tasks.state.revision";
  if (task.status === "CONFIRMED") return "tasks.state.new";
  return `status.${task.status}`;
}

const bucketSlug: Record<Bucket, string> = { "초안": "draft", "내 차례": "mine", "진행 중": "progress", "판정 대기": "review", "검증": "verification", "완료 대기": "await", "완료": "done", "취소": "cancelled" };
const laneSlug: Record<Lane, string> = { "초안": "draft", "준비": "backlog", "진행": "progress", "검증": "verification", "완료": "done" };

/** 지금 이 사람이 손대야 하는 업무. 대시보드의 "해야 할 일"도 같은 판정을 쓴다. */
export const actionable = (task: Task, viewerId?: string) =>
  Boolean(task.scheduleChangeActionRequired)
  || (!(task.status === "REQUESTED" && !task.collaborationSourceDepartmentId && task.issuerId === viewerId)
    && ["내 차례", "판정 대기", "검증", "완료 대기"].includes(taskBucket(task)));

/** 작성 중인 초안을 놓치지 않게 목록과 보드 안에서 먼저 보여준다. */
export function taskOrderRank(task: Task, viewerId?: string): number {
  if (task.status === "DRAFT" && ["QUEUED", "GENERATING", "CANCELLING"].includes(task.aiDraftStatus || "")) return 0;
  if (task.status === "DRAFT" && task.aiDraftStatus === "FAILED") return 1;
  if (task.status === "DRAFT") return 2;
  if (actionable(task, viewerId)) return 3;
  return 4;
}

export function TasksPage({ tasks, mineTaskIds, projects, users, departments, viewerId = "", proposalOnly = false, scopeAvailable, canCreate, initialProject, hasMoreCompleted = false, onOpen, onCreate, onCopy, onMoreCompleted }: {
  tasks: Task[]; projects: Project[]; users: User[]; departments: Department[];
  mineTaskIds: string[]; viewerId?: string; proposalOnly?: boolean; scopeAvailable: boolean; canCreate: boolean; initialProject?: string;
  hasMoreCompleted?: boolean; onOpen: (id: string) => void; onCreate: () => void; onCopy?: (task: Task) => void; onMoreCompleted?: () => void;
}) {
  const { t } = useTranslation();
  const viewer = users.find(user => user.id === viewerId);
  const needsApproval = (task: Task) => taskNeedsApproval(task, viewer, projects.find(project => project.id === task.projectId), departments);
  const [view, setView] = useState<"board" | "list">("board");
  const [copyMode, setCopyMode] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyError, setCopyError] = useState("");
  async function selectTask(id: string) {
    if (copyLoading) return;
    if (!copyMode || !canCreate || !onCopy) { onOpen(id); return; }
    setCopyLoading(true); setCopyError("");
    try {
      // 목록은 첨부와 본문 일부를 생략하므로 복제할 때 원본 상세를 다시 조회한다.
      const source = await api.task(id);
      onCopy(source); setCopyMode(false);
    } catch (reason) { setCopyError(reason instanceof Error ? reason.message : "복제할 업무를 불러오지 못했습니다."); }
    finally { setCopyLoading(false); }
  }
  const [query, setQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [initialPreferences] = useState(() => savedTaskFilters(viewerId));
  const [scope, setScope] = useState<TaskScope>(initialPreferences.scope);
  const [filters, setFilters] = useState<Record<string, string[]>>(() => {
    const saved = initialPreferences.filters;
    const projectIds = new Set(projects.map((project) => project.id));
    const userIds = new Set(users.map((user) => user.id));
    const project = (initialProject ? [initialProject] : saved.project || []).map((value) => {
      if (projectIds.has(value) || value === NO_PROJECT) return value;
      return projects.find((item) => (item.code || item.name) === value)?.id || (["프로젝트", "Project"].includes(value) ? NO_PROJECT : "");
    }).filter(Boolean);
    const owner = (saved.owner || []).map((value) => userIds.has(value) ? value : users.find((user) => user.displayName === value)?.id || "").filter(Boolean);
    const migrated = { ...saved };
    if (project.length) migrated.project = project; else delete migrated.project;
    if (owner.length) migrated.owner = owner; else delete migrated.owner;
    return migrated;
  });
  const [operators, setOperators] = useState<Record<string, FilterOperator>>(initialPreferences.operators);
  const [dueFrom, setDueFrom] = useState(initialPreferences.dueFrom);
  const [dueTo, setDueTo] = useState(initialPreferences.dueTo);

  useEffect(() => {
    try { localStorage.setItem(taskFilterKey(viewerId), JSON.stringify({ scope, filters, operators, dueFrom, dueTo } satisfies TaskFilterPreferences)); }
    catch { /* 브라우저 저장소가 막혀도 현재 화면 필터는 계속 동작한다. */ }
  }, [dueFrom, dueTo, filters, operators, scope, viewerId]);

  const userName = useMemo(() => new Map(users.map((user) => [user.id, user.displayName])), [users]);
  const userById = useMemo(() => new Map(users.map((user) => [user.id, user])), [users]);
  const projectName = useMemo(() => new Map(projects.map((project) => [project.id, project.code || project.name])), [projects]);
  const departmentName = useMemo(() => new Map(departments.map((department) => [department.id, department.name])), [departments]);
  const assigneeText = (task: Task) => task.assigneeIds.length ? task.assigneeIds.map((id) => userName.get(id) || t("tasks.member")).join(", ") : t("tasks.unassigned");
  const mine = useMemo(() => new Set(mineTaskIds), [mineTaskIds]);
  const availableDepartments = useMemo(() => departments.filter((department) => tasks.some((task) => departmentWithin(task.departmentId, department.id, departments))), [departments, tasks]);
  const scopeTasks = tasks.filter((task) => mine.has(task.id)
    || (scope.team && !task.projectId)
    || (scope.projects && Boolean(task.projectId))
    || scope.departmentIds.some((departmentId) => departmentWithin(task.departmentId, departmentId, departments)));

  const groups = [
    { key: "state", name: t("tasks.status"), options: bucketOptions as string[] },
    { key: "owner", name: t("tasks.assignee"), options: [...new Set(scopeTasks.flatMap((task) => task.assigneeIds))] },
    { key: "project", name: t("tasks.project"), options: [
      ...new Set(scopeTasks.map((task) => task.projectId).filter((id): id is string => id != null && projectName.has(id))),
      ...(scopeTasks.some((task) => !task.projectId || !projectName.has(task.projectId)) ? [NO_PROJECT] : []),
    ] },
    { key: "level", name: t("tasks.difficulty"), options: ["상", "중", "하"] },
    { key: "privacy", name: t("tasks.visibility"), options: ["공개", "비공개"] },
  ].filter((group) => group.options.length > 0);

  const optionLabel = (key: string, value: string) => {
    if (key === "state") return t(`tasks.bucket.${bucketSlug[value as Bucket]}`);
    if (key === "owner") return userName.get(value) || t("tasks.memberUnavailable");
    if (key === "project") return value === NO_PROJECT ? t("tasks.noProject") : projectName.get(value) || t("tasks.projectUnavailable");
    if (key === "level") return t(`difficulty.${value === "상" ? "HIGH" : value === "중" ? "MEDIUM" : "LOW"}`);
    if (key === "privacy") return t(value === "공개" ? "tasks.public" : "tasks.private");
    return value;
  };

  const toggle = (key: string, value: string) => setFilters((current) => {
    const next = { ...current };
    const picked = next[key] || [];
    next[key] = picked.includes(value) ? picked.filter((item) => item !== value) : [...picked, value];
    if (!next[key].length) delete next[key];
    return next;
  });
  const clearAll = () => { setFilters({}); setOperators({}); setDueFrom(""); setDueTo(""); };

  const active: { key: string; label: string; clear: () => void }[] = groups.flatMap((group) => (filters[group.key] || []).map((value) => ({
    key: `${group.key}:${value}`,
    label: `${group.name}${operators[group.key] === "exclude" ? ` ${t("tasks.exclude")}` : ""} · ${optionLabel(group.key, value)}`,
    clear: () => toggle(group.key, value),
  })));
  if (dueFrom || dueTo) active.push({ key: "due", label: `${t("tasks.due")} · ${dueFrom || "…"} → ${dueTo || "…"}`, clear: () => { setDueFrom(""); setDueTo(""); } });

  const matches = (task: Task) => {
    const needle = query.trim().toLowerCase();
    if (needle && !`${task.internalTitle} ${userName.get(task.issuerId) || ""} ${assigneeText(task)}`.toLowerCase().includes(needle)) return false;
    if (filters.state && !filters.state.includes(taskBucket(task))) return false;
    if (filters.owner) {
      const selectedAssignee = task.assigneeIds.some((id) => filters.owner.includes(id));
      if (operators.owner === "exclude" ? selectedAssignee : !selectedAssignee) return false;
    }
    if (filters.project && !filters.project.includes(task.projectId && projectName.has(task.projectId) ? task.projectId : NO_PROJECT)) return false;
    if (filters.level && !filters.level.includes(difficultyText[task.difficulty])) return false;
    if (filters.privacy && !filters.privacy.includes(task.private ? "비공개" : "공개")) return false;
    if (dueFrom && (!task.dueOn || isoDate(task.dueOn) < dueFrom)) return false;
    if (dueTo && (!task.dueOn || isoDate(task.dueOn) > dueTo)) return false;
    return true;
  };

  const visible = scopeTasks.filter(matches).sort((a, b) => {
    if (taskOrderRank(a, viewerId) !== taskOrderRank(b, viewerId)) return taskOrderRank(a, viewerId) - taskOrderRank(b, viewerId);
    return (isoDate(a.dueOn) || "9999-99-99").localeCompare(isoDate(b.dueOn) || "9999-99-99");
  });
  const counts = (bucket: Bucket) => scopeTasks.filter((task) => taskBucket(task) === bucket).length;
  const selectedScopeCount = Number(scope.team) + Number(scope.projects) + scope.departmentIds.length;
  const scopeSummary = `${t("tasks.myTasks")}${selectedScopeCount ? t("tasks.selectedScope") : ""} · ${t("tasks.total", { count: scopeTasks.length })}`;
  const toggleDepartment = (departmentId: string) => setScope((current) => ({
    ...current,
    departmentIds: current.departmentIds.includes(departmentId)
      ? current.departmentIds.filter((id) => id !== departmentId)
      : [...current.departmentIds, departmentId],
  }));

  return <>
    <PageHead title={t("tasks.title")} meta={scopeSummary} controls={<>
      <div className="search-field"><Search size={14} /><input aria-label={t("tasks.search")} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("tasks.searchPlaceholder")} /></div>
      <div className="view-switch">
        <button type="button" className={view === "board" ? "on" : ""} title={t("tasks.board")} aria-label={t("tasks.boardView")} aria-pressed={view === "board"} onClick={() => setView("board")}><LayoutGrid size={15} /></button>
        <button type="button" className={view === "list" ? "on" : ""} title={t("tasks.list")} aria-label={t("tasks.listView")} aria-pressed={view === "list"} onClick={() => setView("list")}><List size={15} /></button>
      </div>
      {canCreate && <>
        <button className="primary new-task" disabled={copyLoading} onClick={() => { setCopyMode(false); setCopyError(""); onCreate(); }}><Plus size={15} /> {t(proposalOnly ? "tasks.propose" : "tasks.create")}</button>
        {onCopy && <button type="button" aria-pressed={copyMode} disabled={copyLoading} onClick={() => { setCopyMode(!copyMode); setCopyError(""); }}><Copy size={15} />{copyMode ? "복제 취소" : "복제"}</button>}
      </>}
    </>} />
    {copyMode && <p className="task-copy-hint" role="status">{copyLoading ? <><LoaderCircle size={15} className="spin" />원본을 불러오는 중…</> : <><Copy size={15} />복제할 업무를 선택하세요. 선택한 업무의 내용으로 새 작성 창이 열립니다.</>}</p>}
    {copyError && <p className="global-error" role="alert">{copyError}</p>}

    <div className="filter-bar">
      <button type="button" className={`filter-button ${filterOpen ? "open" : ""} ${active.length ? "armed" : ""}`} onClick={() => { setFilterOpen(!filterOpen); setScopeOpen(false); }}>
        <Filter size={14} /> {t("tasks.filter")}{active.length > 0 && <span className="filter-count mono">{active.length}</span>}
      </button>
      {active.map((chip) => <span className="filter-chip" key={chip.key}>{chip.label}<button type="button" aria-label={t("tasks.clear", { label: chip.label })} onClick={chip.clear}><X size={11} /></button></span>)}
      {active.length > 0 && <button type="button" className="link-button" onClick={clearAll}>{t("tasks.clearAll")}</button>}
      <div className="filter-bar-right">
        <span className="mono filter-tally">{t("tasks.bucket.mine")} {counts("내 차례")} · {t("tasks.lane.progress")} {counts("진행 중")} · {t("tasks.bucket.await")} {counts("완료 대기")} · {t("tasks.bucket.done")} {counts("완료")} · {t("tasks.bucket.draft")} {counts("초안")}</span>
        {scopeAvailable && <button type="button" className={`scope-button ${scopeOpen ? "open" : ""} ${selectedScopeCount ? "armed" : ""}`} aria-expanded={scopeOpen} onClick={() => { setScopeOpen(!scopeOpen); setFilterOpen(false); }}><SlidersHorizontal size={14} /> {t("tasks.scope")}{selectedScopeCount > 0 && <span className="filter-count mono">{selectedScopeCount}</span>}</button>}
      </div>
      {scopeOpen && <div className="scope-panel">
        <header><div><b>{t("tasks.scope")}</b><small>{t("tasks.scopeHelp")}</small></div><button type="button" onClick={() => setScopeOpen(false)} aria-label={t("tasks.closeScope")}><X size={14} /></button></header>
        <div className="scope-presets">
          <label><input type="checkbox" checked={scope.team} onChange={(event) => setScope((current) => ({ ...current, team: event.target.checked }))} /><span><b>{t("tasks.teamTasks")}</b><small>{t("tasks.teamTasksHelp")}</small></span></label>
          <label><input type="checkbox" checked={scope.projects} onChange={(event) => setScope((current) => ({ ...current, projects: event.target.checked }))} /><span><b>{t("tasks.projectTasks")}</b><small>{t("tasks.projectTasksHelp")}</small></span></label>
        </div>
        <fieldset><legend>{t("tasks.departments")}</legend><div className="scope-departments">{availableDepartments.map((department) => <label key={department.id}><input type="checkbox" checked={scope.departmentIds.includes(department.id)} onChange={() => toggleDepartment(department.id)} /><span>{department.name}</span></label>)}</div>{availableDepartments.length === 0 && <p>{t("tasks.noDepartments")}</p>}</fieldset>
        <p className="scope-storage-note">{t("tasks.filterSaved")}</p>
        <footer><button type="button" onClick={() => setScope(defaultTaskFilters.scope)}>{t("tasks.mineOnly")}</button><button type="button" className="primary" onClick={() => setScopeOpen(false)}>{t("tasks.apply")}</button></footer>
      </div>}
      {filterOpen && <div className="filter-panel">
        {groups.map((group) => <div className="filter-group" key={group.key}>
          <header><div className="mono-label">{group.name}</div>{group.key === "owner" && <span className="filter-operator" role="group" aria-label={t("tasks.assigneeOperator")}>
            {(["include", "exclude"] as FilterOperator[]).map((operator) => <button type="button" key={operator} className={(operators.owner || "include") === operator ? "on" : ""}
              aria-pressed={(operators.owner || "include") === operator} onClick={() => setOperators((current) => ({ ...current, owner: operator }))}>{t(`tasks.${operator}`)}</button>)}
          </span>}</header>
          <div>{group.options.map((option) => <button type="button" key={option} className={(filters[group.key] || []).includes(option) ? "picked" : ""} onClick={() => toggle(group.key, option)}>{optionLabel(group.key, option)}</button>)}</div>
        </div>)}
        <div className="filter-group">
          <div className="mono-label">{t("tasks.due")}</div>
          <div className="filter-dates"><input type="date" aria-label={t("tasks.dueFrom")} value={dueFrom} onChange={(event) => setDueFrom(event.target.value)} /><span>→</span><input type="date" aria-label={t("tasks.dueTo")} value={dueTo} onChange={(event) => setDueTo(event.target.value)} /></div>
        </div>
        <p className="scope-storage-note">{t("tasks.filterSaved")}</p>
        <div className="filter-actions"><button type="button" onClick={clearAll}>{t("tasks.reset")}</button><button type="button" className="primary" onClick={() => setFilterOpen(false)}>{t("tasks.apply")}</button></div>
      </div>}
    </div>

    {view === "list"
      ? <div className={`table-card${copyMode ? " task-copy-mode" : ""}`} aria-busy={copyLoading}>
        <div className="task-grid task-grid-head">
          <span>{t("tasks.status")}</span><span>{t("tasks.titleColumn")}</span><span>{t("tasks.assignee")}</span><span data-col-optional="">{t("tasks.issuer")}</span><span>{t("tasks.due")}</span><span>D-day</span><span data-col-narrow="">{t("tasks.difficulty")}</span><span>{t("tasks.visibility")}</span><span data-col-optional="">{t("tasks.round")}</span>
        </div>
        {visible.map((task) => <button className="task-grid task-grid-row" key={task.id} onClick={() => void selectTask(task.id)}>
          <span className={`state-cell bucket-${bucketSlug[taskBucket(task)]}`}><i />{t(taskStateKey(task, viewerId))}</span>
          <span className="title-cell">
            {task.status === "DRAFT" && <em className="draft-badge">{t("tasks.draft")}</em>}
            {["QUEUED", "GENERATING", "CANCELLING"].includes(task.aiDraftStatus || "") && <em className="task-ai-state"><LoaderCircle size={11} className="spin" />{t("tasks.aiWriting")}</em>}
            {task.aiDraftStatus === "FAILED" && <em className="task-ai-state failed"><AlertTriangle size={11} />{t("tasks.aiFailed")}</em>}
            <em className={`project-chip mono ${task.projectId ? "" : "project-empty"}`}>{task.projectId ? projectName.get(task.projectId) || "-" : "-"}</em>
            <b>{task.internalTitle}</b>
            <small>{departmentName.get(task.departmentId) || t("tasks.teamUnassigned")}</small>
          </span>
          <span>{assigneeText(task)}</span>
          <span data-col-optional="">{userName.get(task.issuerId) || "-"}</span>
          <span className="mono">{isoDate(task.dueOn) || t("tasks.unscheduled")}</span>
          <span className={`mono dday ${ddayTone(task)}`}>{ddayText(task)}</span>
          <span data-col-narrow="">{t(`difficulty.${task.difficulty}`)}</span>
          <span className={task.private ? "warn" : ""}>{t(task.private ? "tasks.private" : "tasks.public")}</span>
          <span data-col-optional="" className="mono">{t("tasks.roundCount", { count: task.rounds.length })}</span>
        </button>)}
        {visible.length === 0 && <Empty title={t("tasks.noMatch")} copy={t("tasks.noMatchHelp")} action={<button type="button" className="secondary" onClick={clearAll}>{t("tasks.resetFilters")}</button>} />}
        {visible.length > 0 && <TableFoot count={visible.length} total={scopeTasks.length} hint={copyMode ? "복제할 업무를 선택하세요." : t("tasks.openHint")} />}
        {hasMoreCompleted && onMoreCompleted && <button type="button" className="completed-more" onClick={onMoreCompleted}>{t("tasks.completedMore")}<ChevronDown size={14} /></button>}
      </div>
      : <>
        <div className={`board${copyMode ? " task-copy-mode" : ""}`} aria-busy={copyLoading}>
          {laneOrder.map((lane) => {
            const queueBucket = lane === "진행" ? "판정 대기" : lane === "검증" ? "완료 대기" : null;
            const proposalQueue = lane === "준비";
            const inQueue = (task: Task) => proposalQueue ? task.status === "REQUESTED" && !task.collaborationSourceDepartmentId : !!queueBucket && taskBucket(task) === queueBucket;
            const queue = visible.filter(inQueue);
            const scheduleQueue = lane === "진행" ? visible.filter(task => task.status === "IN_PROGRESS" && task.scheduleChangeRequest?.status === "PENDING") : [];
            const queues = [
              { key: proposalQueue ? "proposal" : lane === "진행" ? "review" : "completion", tasks: queue,
                title: proposalQueue ? t("tasks.state.proposalApproval") : queueBucket ? t(`tasks.bucket.${bucketSlug[queueBucket]}`) : "",
                help: proposalQueue ? "제안된 업무를 확인하고 승인합니다" : t(lane === "진행" ? "tasks.reviewQueueHelp" : "tasks.completionQueueHelp") },
              { key: "schedule", tasks: scheduleQueue, title: t(scheduleQueue.some(task => task.scheduleChangeActionRequired) ? "tasks.state.scheduleApproval" : "tasks.state.schedulePending"), help: t("tasks.scheduleQueueHelp") },
            ].filter(group => group.tasks.length > 0);
            const cards = visible.filter((task) => taskLane(task) === lane && !inQueue(task) && !scheduleQueue.includes(task));
            const count = cards.length + queue.length + scheduleQueue.length;
            return <section className="board-lane" key={lane}>
              <header><span className={`lane-dot lane-${laneSlug[lane]}`} /><b>{t(`tasks.lane.${laneSlug[lane]}`)}</b><span className={`mono${[...cards, ...queue, ...scheduleQueue].some(needsApproval) ? " approval-required" : ""}`}>{count}</span>{canCreate && lane === "초안" && <button type="button" title={t("tasks.add")} aria-label={t("tasks.add")} onClick={onCreate}><Plus size={13} /></button>}</header>
              <div>
                {queues.map(group => <details key={group.key} className={`board-queue queue-${group.key}`}>
                  <summary><span><b>{group.title}</b><small>{group.help}</small></span><em className={`mono${group.tasks.some(needsApproval) ? " approval-required" : ""}`}>{group.tasks.length}</em></summary>
                  <div>{group.tasks.map((task) => <TaskBoardCard key={task.id} task={task} viewerId={viewerId} projectName={projectName} userName={userName} userById={userById} assigneeText={assigneeText} onOpen={selectTask} />)}</div>
                </details>)}
                {count === 0 && <p className="lane-empty">{t("tasks.empty")}</p>}
                {cards.map((task) => <TaskBoardCard key={task.id} task={task} viewerId={viewerId} projectName={projectName} userName={userName} userById={userById} assigneeText={assigneeText} onOpen={selectTask} />)}
                {lane === "완료" && hasMoreCompleted && onMoreCompleted && <button type="button" className="completed-more" onClick={onMoreCompleted}>{t("tasks.completedMore")}<ChevronDown size={14} /></button>}
              </div>
            </section>;
          })}
        </div>
        <p className="board-note">{t("tasks.boardHelp")}</p>
      </>}
  </>;
}

export function CompletedTasksPage({ projects, users, departments, onBack, onOpen }: {
  projects: Project[]; users: User[]; departments: Department[];
  onBack: () => void; onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Task[]>([]);
  const [cursor, setCursor] = useState<string | null>();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const projectName = useMemo(() => new Map(projects.map((project) => [project.id, project.code || project.name])), [projects]);
  const userName = useMemo(() => new Map(users.map((user) => [user.id, user.displayName])), [users]);
  const departmentName = useMemo(() => new Map(departments.map((department) => [department.id, department.name])), [departments]);

  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    void api.completedTasks().then((page) => {
      if (!active) return;
      setItems(page.items); setCursor(page.nextCursor || null);
    }).catch((reason) => {
      if (active) setError(reason instanceof Error ? reason.message : t("tasks.completedLoadError"));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [t]);

  async function loadMore() {
    if (!cursor || loadingMore) return;
    setLoadingMore(true); setError("");
    try {
      const page = await api.completedTasks(cursor);
      setItems((current) => [...current, ...page.items.filter((task) => current.every((item) => item.id !== task.id))]);
      setCursor(page.nextCursor || null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("tasks.completedLoadError"));
    } finally { setLoadingMore(false); }
  }

  return <>
    <PageHead title={t("tasks.completedTitle")} meta={t("tasks.completedMeta", { count: items.length })} controls={
      <button type="button" className="secondary" onClick={onBack}><ArrowLeft size={14} />{t("tasks.backToBoard")}</button>
    } />
    {error && <div className="global-error">{error}</div>}
    {loading ? <Loading layout="table" label={t("tasks.completedLoading")} /> : <div className="table-card">
      <div className="task-grid task-grid-head">
        <span>{t("tasks.status")}</span><span>{t("tasks.titleColumn")}</span><span>{t("tasks.assignee")}</span><span data-col-optional="">{t("tasks.issuer")}</span><span>{t("tasks.completedOn")}</span><span>D-day</span><span data-col-narrow="">{t("tasks.difficulty")}</span><span>{t("tasks.visibility")}</span><span data-col-optional="">{t("tasks.round")}</span>
      </div>
      {items.map((task) => <button className="task-grid task-grid-row" key={task.id} onClick={() => onOpen(task.id)}>
        <span className="state-cell bucket-done"><i />{t("tasks.bucket.done")}</span>
        <span className="title-cell"><em className={`project-chip mono ${task.projectId ? "" : "project-empty"}`}>{task.projectId ? projectName.get(task.projectId) || "-" : "-"}</em><b>{task.internalTitle}</b><small>{departmentName.get(task.departmentId) || t("tasks.teamUnassigned")}</small></span>
        <span>{task.assigneeIds.map((id) => userName.get(id) || t("tasks.member")).join(", ") || t("tasks.unassigned")}</span>
        <span data-col-optional="">{userName.get(task.issuerId) || "-"}</span>
        <span className="mono">{task.completedAt ? isoDate(task.completedAt) : "-"}</span>
        <span className={`mono dday ${ddayTone(task)}`}>{ddayText(task)}</span>
        <span data-col-narrow="">{t(`difficulty.${task.difficulty}`)}</span>
        <span className={task.private ? "warn" : ""}>{t(task.private ? "tasks.private" : "tasks.public")}</span>
        <span data-col-optional="" className="mono">{t("tasks.roundCount", { count: task.rounds.length })}</span>
      </button>)}
      {items.length === 0 && <Empty title={t("tasks.noCompleted")} copy={t("tasks.noCompletedHelp")} />}
      {items.length > 0 && <TableFoot count={items.length} total={items.length} hint={t("tasks.openHint")} />}
      {cursor && <button type="button" className="completed-more" disabled={loadingMore} onClick={() => void loadMore()}>{loadingMore ? <LoaderCircle size={14} className="spin" /> : <ChevronDown size={14} />}{t(loadingMore ? "tasks.completedLoadingMore" : "tasks.completedMore")}</button>}
    </div>}
  </>;
}

function TaskBoardCard({ task, viewerId, projectName, userName, userById, assigneeText, onOpen }: {
  task: Task; projectName: Map<string, string>; userName: Map<string, string>; userById: Map<string, User>;
  viewerId: string; assigneeText: (task: Task) => string; onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const proposal = task.status === "REQUESTED" && !task.collaborationSourceDepartmentId;
  const scheduleChange = task.status === "IN_PROGRESS" ? task.scheduleChangeRequest : null;
  return <button className="board-card" onClick={() => onOpen(task.id)}>
    <KanbanPatternBar users={task.assigneeIds.map((id) => userById.get(id)).filter((user): user is User => Boolean(user))} />
    <span className="board-card-top"><em className={`project-chip mono ${task.projectId ? "" : "project-empty"}`}>{task.projectId ? projectName.get(task.projectId) || "-" : "-"}</em>{["QUEUED", "GENERATING", "CANCELLING"].includes(task.aiDraftStatus || "") ? <em className="task-ai-state"><LoaderCircle size={11} className="spin" />{t("tasks.aiWriting")}</em> : task.aiDraftStatus === "FAILED" ? <em className="task-ai-state failed"><AlertTriangle size={11} />{t("tasks.aiFailed")}</em> : proposal ? <em className="task-ai-state">{t(task.issuerId === viewerId ? "tasks.state.proposalPending" : "tasks.state.proposalApproval")}</em> : scheduleChange ? <em className="task-ai-state schedule-change">{t(task.scheduleChangeActionRequired ? "tasks.state.scheduleApproval" : "tasks.state.schedulePending")}</em> : <em className={task.private ? "warn" : "muted"}>{t(task.private ? "tasks.private" : "tasks.public")}</em>}</span>
    <b>{task.internalTitle}</b>
    <span className="board-card-bottom"><Avatar name={userName.get(task.assigneeIds[0]) || "?"} userId={userById.get(task.assigneeIds[0])?.id} avatarVersion={userById.get(task.assigneeIds[0])?.avatarVersion} size="sm" /><span>{assigneeText(task)}</span><span className="board-card-meta"><span className="muted">{t(`difficulty.${task.difficulty}`)}</span></span></span>
    <TaskDeadline task={task} />
  </button>;
}
